import React, { useState, useMemo, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { withStyles } from '@material-ui/core/styles';
import { useQuery } from 'react-query';
import * as API from 'api';

import MuiPaper from '@material-ui/core/Paper';
import MuiTextField from '@material-ui/core/TextField';
import AlertToast from 'components/AlertToast';

import MuiButton from '@material-ui/core/Button';
import MuiGrid from '@material-ui/core/Grid';
import MuiAddIcon from '@material-ui/icons/Add';
import MuiInputAdornment from '@material-ui/core/InputAdornment';
import MuiSearchIcon from '@material-ui/icons/Search';
import MuiFormControlLabel from '@material-ui/core/FormControlLabel';
import MuiSwitch from '@material-ui/core/Switch';
import MuiFormControl from '@material-ui/core/FormControl';
import MuiInputLabel from '@material-ui/core/InputLabel';
import MuiSelect from '@material-ui/core/Select';
import MuiMenuItem from '@material-ui/core/MenuItem';

import ViewWrapper from 'views/ViewWrapper';
import TableReleases from 'components/TableReleases/release-table';
import DialogCreateRelease from 'components/DialogCreateRelease';
import { useDeliverDistroQueueEntry } from '../../data-client/use-asset-distribution-queue';
import { isUpc } from 'utils/helpers';
const ReleasesAPI = API.Releases();
const CodesAPI = API.Codes();
const DiscoveryModeTracksAPI = API.DiscoveryModeTracks();

const styles = theme => ({
  paper: {
    overflow: 'hidden',
    padding: theme.spacing(2),
  },
  title: {
    paddingBottom: theme.spacing(2),
    textTransform: 'capitalize',
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(1),
  },
  actionButton: {
    marginRight: theme.spacing(1),
  },
  toggleLabel: {
    marginRight: 0,
  },
  startLabel: {
    marginRight: theme.spacing(1),
  },
  endLabel: {
    marginLeft: theme.spacing(0.5),
  },
  codeStatus: {
    padding: theme.spacing(1, 0),
  },
  sourceSelect: {
    minWidth: 170,
  },
});

// Where releases come from: created in Venice or through the aep-comanager API.
const RELEASE_SOURCES = [
  { value: 'all', label: 'All releases' },
  { value: 'aep-comanager', label: 'aep-comanager only' },
  { value: 'venice', label: 'Venice only' },
];

const Releases = ({ ...props }) => {
  const {
    classes,
    match: {
      params: { paymentAccountId },
    },
  } = props;

  const [isCreateReleaseDialogOpen, setIsCreateReleaseDialogOpen] = useState(false);
  const [searchObj, setSearchObj] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [source, setSource] = useState('all');
  const [params, setParams] = useState({ page: 0, rows_per_page: 25 });
  const [pageError, setPageError] = useState();
  const [isExportingDiscoveryModeFile, setExportingDiscoveryModeFile] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const { mutateAsync: deliverDistroQueueEntryMutation } = useDeliverDistroQueueEntry({
    onError: error => setPageError(error.toString()),
  });

  function handleChangePage(_event, newPage) {
    setParams({ ...params, page: newPage });
  }

  function handleChangeRowsPerPage({ target: { value: newRowsPerPage } }) {
    setParams({ ...params, page: 0, rows_per_page: newRowsPerPage });
  }

  function clearPageError() {
    setPageError(undefined);
  }

  async function startDistroQueueDeliver() {
    const commaSeperatedAssetReleaseDistributionQueueIds = prompt(
      'What is the distribution queue id you want to deliver?'
    );
    const assetReleaseDistributionQueueIds = commaSeperatedAssetReleaseDistributionQueueIds
      .split(',')
      .filter(id => id)
      .map(id => id.trim());

    const errors = [];
    for (let assetReleaseDistributionQueueId of assetReleaseDistributionQueueIds) {
      try {
        if (isNaN(parseInt(assetReleaseDistributionQueueId, 10))) {
          throw new Error(`Invalid queue id '${assetReleaseDistributionQueueId}'`);
        }
        //Add a slight wait to not pound the backend system.
        await new Promise(resolve => setTimeout(resolve, 100));

        await deliverDistroQueueEntryMutation({ assetReleaseDistributionQueueId });
      } catch (error) {
        errors.push(`Could not queue delivery ${assetReleaseDistributionQueueId}, ${error}`);
      }
    }

    if (errors.length > 0) {
      setPageError('Could not deliver all queue entries. Please check console for more details');
      console.error(errors.join('\n'));
    }
  }

  async function exportDraftCampaign() {
    if (
      window.confirm(
        'Doing this will lock the current Spotify Discovery Mode campaign selections. Do you want to continue?'
      )
    ) {
      setExportingDiscoveryModeFile(true);
      const response = await DiscoveryModeTracksAPI.exportDraftCampaign();

      if (!response?.data?.url) {
        alert('No data available to export.');
        setExportingDiscoveryModeFile(false);
        return;
      }

      const link = document.createElement('a');
      link.href = response?.data?.url;
      link.click();
      setExportingDiscoveryModeFile(false);
    }
  }

  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        if (!value) return setSearchObj({});
        setSearchObj({ type: isUpc(value) ? 'upc' : 'q', value });
      }, 350),
    []
  );

  const handleSearch = event => {
    setSearchValue(event.target.value);
    debouncedSearch(event.target.value);
  };

  const {
    data: releasesData = {},
    isLoading,
    isRefetching,
    refetch,
  } = useQuery(
    ['getAllWithQuery', params.page, params.rows_per_page, showArchived, searchObj, paymentAccountId, source],
    () =>
      ReleasesAPI.getAllWithQuery({
        params: {
          is_hidden: showArchived,
          payment_account_id: paymentAccountId,
          source,
          ...{ [searchObj.type]: searchObj.value },
          ...params,
        },
      }),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: upcPrefixData, isLoading: upcPrefixIsLoading } = useQuery(
    ['getCurrentUpcPrefix'],
    () => CodesAPI.getCurrentUpcPrefix(),
    {
      refetchOnWindowFocus: false,
      select: resp => resp.data,
    }
  );

  const { data: isrcPrefixData, isLoading: isrcPrefixIsLoading } = useQuery(
    ['getCurrentIsrcPrefix'],
    () => CodesAPI.getCurrentIsrcPrefix(),
    {
      refetchOnWindowFocus: false,
      select: resp => resp.data,
    }
  );

  async function handleReleaseCreation() {
    await refetch();
    setIsCreateReleaseDialogOpen(false);
  }

  useEffect(() => {
    // Add a timeout to allow the page to load before the search is triggered
    const upc = new URLSearchParams(window.location.search).get('upc');
    if (upc) {
      setSearchValue(upc);
      debouncedSearch(upc);
    }
  }, []);

  return (
    <ViewWrapper>
      <AlertToast message={pageError} onClear={clearPageError} />
      <MuiGrid item xs={12}>
        <MuiPaper className={classes.paper}>
          {!paymentAccountId && (
            <div className={classes.actionRow}>
              <MuiTextField
                variant="outlined"
                label="Title, Artist or UPC"
                placeholder="Release title, artist or UPC"
                onChange={handleSearch}
                value={searchValue}
                InputProps={{
                  startAdornment: (
                    <MuiInputAdornment position="start">
                      <MuiSearchIcon />
                    </MuiInputAdornment>
                  ),
                }}
              />
              <MuiFormControl variant="outlined" size="small" className={classes.sourceSelect}>
                <MuiInputLabel id="release-source-label">Source</MuiInputLabel>
                <MuiSelect
                  labelId="release-source-label"
                  label="Source"
                  value={source}
                  onChange={event => {
                    setSource(event.target.value);
                    setParams(current => ({ ...current, page: 0 }));
                  }}>
                  {RELEASE_SOURCES.map(option => (
                    <MuiMenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MuiMenuItem>
                  ))}
                </MuiSelect>
              </MuiFormControl>
              <div>
                <span className={classes.startLabel}>Show Active</span>
                <MuiFormControlLabel
                  className={classes.toggleLabel}
                  control={
                    <MuiSwitch
                      checked={showArchived}
                      onChange={event => {
                        event.preventDefault();
                        setShowArchived(!showArchived);
                      }}
                      name="showArchived"
                      inputProps={{ 'aria-label': 'secondary checkbox' }}
                    />
                  }
                />
                <span className={classes.endLabel}>Show Archived</span>
              </div>
              <MuiButton
                variant="contained"
                size="small"
                className={classes.actionButton}
                startIcon={<MuiAddIcon />}
                onClick={() => setIsCreateReleaseDialogOpen(true)}>
                Create Release
              </MuiButton>
              <MuiButton variant="contained" size="small" onClick={startDistroQueueDeliver}>
                Deliver Queue Entry
              </MuiButton>
              <MuiButton
                variant="contained"
                size="small"
                onClick={exportDraftCampaign}
                disabled={isExportingDiscoveryModeFile}>
                Export Discovery Mode CSV
              </MuiButton>
            </div>
          )}
          {upcPrefixIsLoading || isrcPrefixIsLoading ? (
            'Loading...'
          ) : (
            <div className={classes.codeStatus}>
              <div>
                {' '}
                Active UPC:{' '}
                <span style={{ fontWeight: 700 }}>
                  {upcPrefixData.usage_count}/{upcPrefixData.max_count}
                </span>{' '}
                -- {upcPrefixData.prefix}
              </div>
              <div>
                Active ISRC: <span style={{ fontWeight: 700 }}>{isrcPrefixData.usage_count}/99999</span> --{' '}
                {isrcPrefixData.country_code}
                {isrcPrefixData.company_code}
                {isrcPrefixData.year}
              </div>
            </div>
          )}
          <TableReleases
            payment_account_id={paymentAccountId}
            releasesData={releasesData}
            isLoading={isLoading}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            isRefetching={isRefetching}
            refetch={refetch}
            params={params}
          />
          <DialogCreateRelease
            open={isCreateReleaseDialogOpen}
            searchObj={searchObj}
            onClose={() => setIsCreateReleaseDialogOpen(false)}
            onCreate={handleReleaseCreation}
          />
        </MuiPaper>
      </MuiGrid>
    </ViewWrapper>
  );
};

export default withStyles(styles)(Releases);
